import { MarketingLayout } from "@/components/MarketingLayout";

export default function Terms() {
  return (
    <MarketingLayout>
      <article className="mx-auto w-full max-w-3xl px-6 py-16 text-slate-700">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-slate-500">Effective: April 30, 2026</p>

        <p className="mt-8 leading-relaxed">
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of
          Reputely&rsquo;s services available at reputely.co (the
          &ldquo;Service&rdquo;). By using the Service you agree to these Terms.
          If you do not agree, do not use the Service.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">1. Who we are</h2>
        <p className="mt-3 leading-relaxed">
          The Service is operated by Reputely, based in the United States.
          Contact: audits@reputely.co. We are not affiliated with, endorsed
          by, or sponsored by Amazon.com, Inc.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          2. What Reputely does
        </h2>
        <p className="mt-3 leading-relaxed">
          Reputely identifies Amazon customer reviews that may violate
          Amazon&rsquo;s published Community Guidelines and Customer Review
          Policies, drafts takedown requests, and submits them through
          Amazon&rsquo;s official report channels on behalf of the seller. We
          do not access your Amazon seller account.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          3. Eligibility
        </h2>
        <p className="mt-3 leading-relaxed">
          You must be at least 18 years old and authorized to act on behalf of
          the Amazon seller whose products you submit for analysis. By using
          the Service you represent that you meet these requirements.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          4. Free audit
        </h2>
        <p className="mt-3 leading-relaxed">
          The audit is free. By submitting Amazon Standard Identification
          Numbers (ASINs) or a storefront URL, you authorize us to retrieve
          and analyze the public reviews associated with those products. The
          audit produces a report of reviews we believe may be removable
          under Amazon&rsquo;s published policies.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          5. Subscription plans and fees
        </h2>
        <p className="mt-3 leading-relaxed">
          Paid Reputely services are offered as subscription plans with the
          prices, usage limits, cadence, and included features presented at
          sign-up. Plans may include continuous monitoring, policy detection,
          appeal or submission drafting, alerting, and
          submission workflows. Enterprise services or legacy success-fee
          arrangements may be governed by separate written terms accepted by
          the customer.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          6. Acceptable use
        </h2>
        <p className="mt-3 leading-relaxed">You agree not to:</p>
        <ul className="mt-3 list-disc pl-6 leading-relaxed space-y-1">
          <li>Use the Service to remove honest, good-faith customer reviews;</li>
          <li>
            Submit products for analysis that you do not own or are not
            authorized to manage;
          </li>
          <li>
            Use the Service in violation of Amazon&rsquo;s terms or any
            applicable law;
          </li>
          <li>
            Attempt to reverse-engineer, scrape, or interfere with our
            systems;
          </li>
          <li>
            Resell, sublicense, or repackage the Service without our written
            permission.
          </li>
        </ul>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          7. No guarantee of removal
        </h2>
        <p className="mt-3 leading-relaxed">
          Amazon makes the final decision on every review-removal request. We
          do not and cannot guarantee that any specific review will be
          removed. Our commitment is to identify policy violations, draft
          accurate takedown requests, and submit them through Amazon&rsquo;s
          official channels.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          8. Disclaimer of warranties
        </h2>
        <p className="mt-3 leading-relaxed">
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE
          WILL BE UNINTERRUPTED, ERROR-FREE, OR PRODUCE ANY PARTICULAR
          OUTCOME.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          9. Limitation of liability
        </h2>
        <p className="mt-3 leading-relaxed">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, REPUTELY WILL NOT BE LIABLE
          FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
          DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA, EVEN IF ADVISED OF
          THE POSSIBILITY. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM
          ARISING FROM OR RELATED TO THE SERVICE WILL NOT EXCEED THE AMOUNT
          YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM AROSE, OR $100,
          WHICHEVER IS GREATER.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          10. Indemnification
        </h2>
        <p className="mt-3 leading-relaxed">
          You agree to defend, indemnify, and hold harmless Reputely from any
          claim, demand, damage, or expense arising from your misuse of the
          Service, your violation of these Terms, or any third-party claim
          that a review you submitted for analysis was a legitimate good-faith
          customer review.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          11. Termination
        </h2>
        <p className="mt-3 leading-relaxed">
          We may suspend or terminate your access at any time for any reason,
          including violation of these Terms. You may stop using the Service
          at any time. Sections 7–14 survive termination.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          12. Modifications
        </h2>
        <p className="mt-3 leading-relaxed">
          We may revise these Terms by posting an updated version on
          reputely.co. Material changes will be reflected in an updated
          &ldquo;Effective&rdquo; date. Continued use of the Service after
          changes take effect constitutes your acceptance of the revised
          Terms.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          13. Governing law
        </h2>
        <p className="mt-3 leading-relaxed">
          These Terms are governed by the laws of the State of Florida,
          without regard to its conflict-of-laws principles. Subject to
          Section 14 below, any action permitted to be brought in court must
          be filed in the state or federal courts located in Miami-Dade
          County, Florida.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          14. Binding arbitration and class-action waiver
        </h2>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">Please read carefully.</strong>{" "}
          This section affects your legal rights.
        </p>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">Arbitration agreement.</strong>{" "}
          Except as provided below, any dispute, claim, or controversy
          arising from or relating to these Terms or the Service (a
          &ldquo;Dispute&rdquo;) will be resolved by final and binding
          arbitration administered by the American Arbitration Association
          (AAA) under its Commercial Arbitration Rules then in effect. The
          arbitration will be conducted by a single arbitrator in Miami-Dade
          County, Florida (or by videoconference if the parties agree). The
          arbitrator&rsquo;s decision will be final and may be entered as a
          judgment in any court of competent jurisdiction.
        </p>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">Class-action waiver.</strong>{" "}
          You and Reputely each agree that any Dispute will be brought only
          in an individual capacity and not as a plaintiff or class member in
          any purported class, collective, consolidated, or representative
          proceeding. The arbitrator may not consolidate the claims of more
          than one party and may not preside over any form of representative
          or class proceeding. If this class-action waiver is found
          unenforceable, then the entirety of this Section 14 will be void.
        </p>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">Exceptions.</strong> Either
          party may (a) bring an individual claim in small-claims court if
          the claim qualifies, and (b) seek injunctive or other equitable
          relief in court to protect intellectual-property rights, trade
          secrets, or confidential information.
        </p>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">30-day opt-out.</strong> You may
          opt out of this arbitration agreement by sending written notice to
          audits@reputely.co within 30 days of your first use of the Service.
          The notice must include your name, address, the email associated
          with your account, and a clear statement that you wish to opt out
          of arbitration. Opting out will not affect any other portion of
          these Terms.
        </p>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">
            Waiver of jury trial.
          </strong>{" "}
          To the extent permitted by law, you and Reputely each waive any
          right to a trial by jury for any Dispute.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          15. Contact
        </h2>
        <p className="mt-3 leading-relaxed">
          Questions about these Terms can be sent to audits@reputely.co.
        </p>
      </article>
    </MarketingLayout>
  );
}
